import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, SquarePen } from 'lucide-react';
import Header from '@ajat/design-system/Header';
import Input from '@ajat/design-system/Input';
import Button from '@ajat/design-system/Button';
import Hyperlink from '@ajat/design-system/Hyperlink';
import SidePanel from '@ajat/design-system/SidePanel';
import { CheckboxMedium } from '@ajat/design-system/Checkbox';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import useCheckout from '../../Common/Checkout/useCheckout';
import OrderDetailsPanel from '../../Common/Checkout/OrderDetailsPanel';
import KustomCheckoutFrame from '../../Common/Checkout/KustomCheckoutFrame';
import { LegalSections } from '../../Common/HelpCenter';
import { PRIVACY_TITLE, PRIVACY_SECTIONS } from '../HelpCenter/legalContent';
import { copyright } from '../Common/Footer';
import { useCart } from '../../../../context/CartContext';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useSchools from '../../../../hooks/useSchools';

import { CMS_API_URL } from '../../../../config/apiBase';
const BRAND_SLUG = 'clsse';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-SE — Checkout Page
 * 3-step flow:
 *   1. Personnummer input
 *   2. Populated form (validated, read-only feeling)
 *   3. Compressed info + Delivery + Payment selection
 * ══════════════════════════════════════════════════════════════════════════ */

/* Customers paste 20021011-0847 / 021011-0847 style values — keep digits
   only and cap at the 12-digit ÅÅÅÅMMDDXXXX form as they type. */
const sanitizePersonnummer = (v) => v.replace(/\D/g, '').slice(0, 12);

/* ── Personnummer lookup (Checkbiz proxy + mock for 111111111111) ── */
const lookupPersonnummer = async (pnr) => {
  const res = await fetch(`${CMS_API_URL}/api/personnummer/${BRAND_SLUG}/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ personnummer: pnr }),
  });
  if (!res.ok) {
    let msg = 'Ogiltigt personnummer';
    try { msg = (await res.json())?.error || msg; } catch { /* noop */ }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/* ── Class name options (searchable dropdown) ── */
const CLASS_NOT_IN_LIST = '__not_in_list__';
const CLASS_NOT_IN_LIST_OPTION = { label: 'Min klass finns inte i listan', value: CLASS_NOT_IN_LIST };

/* ── Delivery & Payment options ── */
const DELIVERY_OPTIONS = [
  {
    id: 'school',
    label: 'School Delivery',
    description: 'Free shipping · Delivery date: 19 Jan. – 23 Feb.',
  },
  {
    id: 'home',
    label: 'Home Delivery',
    description: 'Free shipping · Delivery date: 19 Jan. – 23 Feb.',
  },
];

const NO_PAYMENT_METHODS_MESSAGE =
  'Onlinebetalning är inte tillgänglig just nu, så din beställning kan inte slutföras. Försök igen senare eller kontakta oss.';

/* ── Validators (Swedish messages) ── */
const VALIDATORS = {
  firstName: (v) => v?.trim() ? '' : 'Ange förnamn',
  lastName:  (v) => v?.trim() ? '' : 'Ange efternamn',
  email:     (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') ? '' : 'Ogiltig e-postadress',
  phone:     (v) => /^\d{7,15}$/.test((v || '').replace(/[\s\-+]/g, '')) ? '' : 'Ogiltigt telefonnummer',
  address:   (v) => v?.trim() ? '' : 'Ange adress',
  postnummer:(v) => /^\d{5}$/.test((v || '').replace(/\s/g, '')) ? '' : 'Postnummer ska vara 5 siffror',
  city:      (v) => v?.trim() ? '' : 'Ange stad',
  className: (v) => v?.trim() ? '' : 'Välj klass',
};

const Checkout = () => {
  const navigate = useNavigate();
  const { school: cartSchool, invoiceFeeOverride, products: cartProducts, packages: cartPackages, total: cartTotal } = useCart(BRAND_SLUG);
  /* Fully discounted cart (e.g. a 100% sales-rep code): the payment gateway
   * is skipped entirely — no method selection, no fee; the BE completes the
   * order as paid and the FE goes straight to the thank-you page. */
  const isFreeOrder = (Number(cartTotal) || 0) <= 0;
  const brandSettings = useBrandSettings(BRAND_SLUG);

  // Sales rep assigned to the selected school (for the "contacted by rep" tick).
  const { schools } = useSchools(BRAND_SLUG);

  /* ── School selection at checkout (collection-link carts ONLY) ──
   * A cart whose lines were added from a /collection/{slug} link carries
   * options.collectionSlug and no school — the checkout collects school +
   * class instead (payload-only school binding: order stamping + rep
   * attribution, never pricing, so the collection tier holds). The regular
   * CLS-SE open-shop checkout is unchanged (no picker, no school gate).
   * NOTE: `useSchools` ids are STRINGS, the cart school id is a NUMBER.
   */
  const hasCollectionLine = useMemo(
    () => [...(cartProducts ?? []), ...(cartPackages ?? [])].some((it) => it?.options?.collectionSlug),
    [cartProducts, cartPackages],
  );
  const needsSchoolPicker = !cartSchool?.id && hasCollectionLine;
  const [selectedSchoolId, setSelectedSchoolId] = useState(null);
  const [schoolSearch, setSchoolSearch] = useState('');
  const SCHOOL_OPTIONS = useMemo(() => schools.map((s) => ({
    label: s.city ? `${s.name}, ${s.city}` : s.name,
    value: String(s.id),
  })), [schools]);
  const effectiveSchoolId = cartSchool?.id ?? (needsSchoolPicker ? selectedSchoolId : null);
  const selectedSchool = useMemo(
    () => schools.find((s) => String(s.id) === String(effectiveSchoolId)) ?? null,
    [schools, effectiveSchoolId],
  );
  const salesRep = selectedSchool?.salesRep ?? null;
  const [contactedByRep, setContactedByRep] = useState(false);

  // School required only for collection-link carts (validators are read live).
  const validators = useMemo(() => (
    needsSchoolPicker ? { ...VALIDATORS, schoolId: (v) => (v ? '' : 'Välj skola') } : VALIDATORS
  ), [needsSchoolPicker]);

  const paymentOptions = useMemo(() => {
    // No fallback: an empty list means ordering is unavailable.
    const list = brandSettings?.paymentMethods ?? [];
    return list.map((m) => {
      const cfg = m.configPublic ?? {};
      const baseFee = Number(cfg.invoice_fee_amount) || 0;
      // Shop's invoice-fee override (if any) wins over the method's config fee.
      const fee = ('invoice_fee_amount' in cfg && invoiceFeeOverride != null) ? Number(invoiceFeeOverride) : baseFee;
      const feeLabel = cfg.invoice_fee_label || 'Fakturaavgift';
      const baseDescription = m.description ?? '';
      const description = fee > 0
        ? `${baseDescription}${baseDescription ? ' · ' : ''}${feeLabel}: +${Math.round(fee)}:-`
        : baseDescription;
      return {
        id: m.id,
        key: m.key,
        label: m.name,
        description,
        fee,
        feeLabel,
      };
    });
  }, [brandSettings?.paymentMethods, invoiceFeeOverride]);

  /* ── SE-brand: Personnummer state ── */
  const [personnummer, setPersonnummer] = useState('');
  const [pnrError, setPnrError] = useState('');
  const [lookingUp, setLookingUp] = useState(false);

  const CLASS_OPTIONS = useMemo(() => {
    // Cart-bound school first; else the school picked in the checkout form.
    const classNames = cartSchool?.classes ?? selectedSchool?.classes ?? [];
    const fromSchool = classNames.map((name) => ({
      label: `Klass ${name}`,
      value: name,
    }));
    return [...fromSchool, CLASS_NOT_IN_LIST_OPTION];
  }, [cartSchool?.classes, selectedSchool?.classes]);

  const {
    step, leaving,
    formData, setFormData,
    touched, setTouched,
    delivery, setDelivery,
    payment, setPayment,
    checkingOut,
    checkoutError,
    iframePayment,
    cancelIframePayment,
    privacyOpen, setPrivacyOpen,
    detailsOpen, setDetailsOpen,
    priceDisplay,
    updateField,
    isFieldValid, fieldError, handleBlur, handleFocus,
    validateAll,
    goToStep,
    handleCheckout,
    handleEditInfo,
    total,
  } = useCheckout({
    brandSlug: BRAND_SLUG,
    brand: 'CLS',
    priceSuffix: ':-',
    thankYouRoute: '/clothing/cls-se/thank-you',
    initialFormData: null,
    validators,
    editStep: 2,
    buildCheckoutPayload: ({ formData: fd, delivery: d, payment: p }) => ({
      personnummer,
      payment_method_id:    isFreeOrder ? null : p,
      school_id:            cartSchool?.id ?? (needsSchoolPicker && selectedSchoolId ? Number(selectedSchoolId) : null),
      school_class:         fd?.className ?? null,
      customer_first_name:  fd?.firstName ?? null,
      customer_last_name:   fd?.lastName ?? null,
      customer_email:       fd?.email ?? null,
      customer_phone:       fd?.phone ?? null,
      customer_address:     fd?.address ?? null,
      customer_postal_code: fd?.postnummer ?? null,
      customer_city:        fd?.city ?? null,
      contacted_by_rep:     salesRep ? contactedByRep : false,
    }),
  });

  // Pre-select the first available payment method (customers rarely change
  // it, and an unselected radio blocked the CTA needlessly).
  useEffect(() => {
    if (payment == null && paymentOptions.length > 0) {
      setPayment(paymentOptions[0].id);
    }
  }, [payment, paymentOptions, setPayment]);

  /* ── Selected payment fee → adjusts displayed total ── */
  const selectedPaymentOption = useMemo(
    () => paymentOptions.find((o) => o.id === payment),
    [paymentOptions, payment],
  );
  const selectedFee = isFreeOrder ? 0 : (Number(selectedPaymentOption?.fee) || 0);
  const totalWithFee = (Number(total) || 0) + selectedFee;
  const displayPrice = `${Math.round(totalWithFee)}:-`;

  /* ── Class name state ──
   * `classCustomMode=false` → searchable dropdown (default view)
   * `classCustomMode=true`  → free-text input with Clear button
   * `classSearch` mirrors the typed query for the searchable variant
   * (pattern reused from SchoolSelector); the committed value lives in
   * formData.className and is set only via onSelect.
   */
  const [classCustomMode, setClassCustomMode] = useState(false);
  const [classSearch, setClassSearch] = useState('');

  const handleClassSearchChange = useCallback((e) => {
    const val = e.target.value;
    setClassSearch(val);
    // Commit the typed value so manual entries (no dropdown selection)
    // still pass validation.
    setFormData((prev) => prev ? { ...prev, className: val } : prev);
    setTouched((prev) => ({ ...prev, className: false }));
  }, [setFormData, setTouched]);

  const handleClassSelect = useCallback((opt) => {
    if (!opt) {
      setClassSearch('');
      setFormData((prev) => prev ? { ...prev, className: '' } : prev);
      return;
    }
    if (opt.value === CLASS_NOT_IN_LIST) {
      // Switch to free-text mode, reset any typed/committed value
      setClassCustomMode(true);
      setClassSearch('');
      setFormData((prev) => prev ? { ...prev, className: '' } : prev);
      setTouched((prev) => ({ ...prev, className: false }));
      return;
    }
    setClassSearch(opt.label);
    setFormData((prev) => prev ? { ...prev, className: opt.value } : prev);
  }, [setFormData, setTouched]);

  const handleClassCustomChange = useCallback((e) => {
    const val = e.target.value;
    if (val === '') {
      // Clear button pressed → revert to searchable dropdown (default view)
      setClassCustomMode(false);
      setClassSearch('');
      setFormData((prev) => prev ? { ...prev, className: '' } : prev);
      setTouched((prev) => ({ ...prev, className: false }));
      return;
    }
    setFormData((prev) => prev ? { ...prev, className: val } : prev);
    // Hide any stale error while the user is typing; re-validates on blur
    setTouched((prev) => ({ ...prev, className: false }));
  }, [setFormData, setTouched]);

  /* ── School picker handlers (collection-link carts only) ── */
  const handleSchoolSearchChange = useCallback((e) => {
    setSchoolSearch(e.target.value);
  }, []);

  const handleSchoolSelect = useCallback((opt) => {
    if (!opt) {
      setSchoolSearch('');
      setSelectedSchoolId(null);
      setFormData((prev) => prev ? { ...prev, schoolId: '' } : prev);
      return;
    }
    setSchoolSearch(opt.label);
    setSelectedSchoolId(opt.value);
    setFormData((prev) => prev ? { ...prev, schoolId: opt.value } : prev);
  }, [setFormData]);

  // Changing the picked school invalidates the class chosen for the old one.
  useEffect(() => {
    if (!needsSchoolPicker) return;
    setClassSearch('');
    setClassCustomMode(false);
    setFormData((prev) => prev ? { ...prev, className: '' } : prev);
    setTouched((prev) => ({ ...prev, className: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolId]);

  /* ── Step 1 → 2: validate personnummer ── */
  /* ── Step 1 → 2 without lookup: fill everything manually ── */
  const handleManualFill = useCallback(() => {
    setPnrError('');
    setFormData({
      firstName: '', lastName: '', email: '', phone: '',
      address: '', postnummer: '', city: '', className: '',
    });
    goToStep(2);
  }, [setFormData, goToStep]);

  const handlePersonnummerSubmit = useCallback(async () => {
    if (!personnummer.trim()) {
      setPnrError('Ange personnummer');
      return;
    }
    if (personnummer.trim().length !== 12) {
      setPnrError('Personnumret ska vara 12 siffror (ÅÅÅÅMMDDXXXX)');
      return;
    }
    setPnrError('');
    setLookingUp(true);
    try {
      const data = await lookupPersonnummer(personnummer.trim());
      setFormData({ ...data, className: '' });
      setTouched({ firstName: true, lastName: true, email: true, phone: true, address: true, postnummer: true, city: true });
      goToStep(2);
    } catch (e) {
      // A lookup-SERVICE failure (503/5xx/network) must never block the
      // checkout: open the form empty and tell the customer why. Only a
      // real "invalid personnummer" (422) keeps the inline error so a
      // typo can be corrected.
      const serviceDown = !e?.status || e.status >= 500;
      if (serviceDown) {
        pushNotification({
          type: 'warning',
          title: 'Personnummersökning',
          message: e?.message || 'Söktjänsten är inte tillgänglig — fyll i uppgifterna manuellt.',
          duration: 6000,
        });
        handleManualFill();
      } else {
        setPnrError(e?.message || 'Ogiltigt personnummer');
      }
    } finally {
      setLookingUp(false);
    }
  }, [personnummer, setFormData, setTouched, goToStep, handleManualFill]);

  /* ── Step 2 → 3 ── */
  const handleContinueToStep3 = useCallback(() => {
    if (validateAll()) goToStep(3, { scrollTop: true });
  }, [validateAll, goToStep]);

  const handleStep2Submit = useCallback((e) => {
    e.preventDefault();
    handleContinueToStep3();
  }, [handleContinueToStep3]);

  /* ── Clear personnummer (step 2) → back to step 1 ── */
  const handleClearPersonnummer = useCallback(() => {
    goToStep(1, {
      beforeTransition: () => {
        setPersonnummer('');
        setFormData(null);
        setPnrError('');
      },
    });
  }, [goToStep, setFormData]);

  /* ══════════════════════════════════════════════════════════════════════ */
  /* Render helpers                                                       */
  /* ══════════════════════════════════════════════════════════════════════ */

  const renderTotalPrice = () => (
    <div className="checkout__total">
      <span className="checkout__total-label typo-h6">TOTALT</span>
      <span className="checkout__total-price typo-h2">{displayPrice}</span>
      <Hyperlink variant="small" className="checkout__total-link" onClick={(e) => { e.preventDefault(); setDetailsOpen(true); }}>Se orderdetaljer</Hyperlink>
    </div>
  );

  const renderPrivacyLink = () => (
    <div className="checkout__privacy">
      <Hyperlink variant="small" onClick={(e) => { e.preventDefault(); setPrivacyOpen(true); }}>{PRIVACY_TITLE}</Hyperlink>
    </div>
  );

  /* ── Step 1: Personnummer input ── */
  const renderStep1 = () => (
    <div className={`checkout__step-wrap${leaving ? ' checkout__step-wrap--leaving' : ''}`}>
      <div className="checkout__form-card">
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              type="action"
              label="Personnummer"
              value={personnummer}
              onChange={(e) => {
                setPersonnummer(sanitizePersonnummer(e.target.value));
                if (pnrError) setPnrError('');
              }}
              onAction={handlePersonnummerSubmit}
              actionIcon={<ArrowRight size={20} />}
              inputMode="numeric"
              maxLength={12}
              error={pnrError}
              showIcon={false}
              disabled={lookingUp}
            />
          </div>
        </div>
      </div>
      <div className="checkout__privacy">
        <Hyperlink variant="small" onClick={(e) => { e.preventDefault(); handleManualFill(); }}>
          Fyll i uppgifterna manuellt
        </Hyperlink>
      </div>
      {renderPrivacyLink()}
      {lookingUp && (
        <div className="checkout__loading">
          <div className="checkout__spinner" />
        </div>
      )}
      {renderTotalPrice()}
    </div>
  );

  /* ── Step 2: Populated form ── */
  const renderStep2 = () => (
    <form onSubmit={handleStep2Submit} className={`checkout__step-wrap${leaving ? ' checkout__step-wrap--leaving' : ''}`}>
      <div className="checkout__form-card">
        {/* Row 0: School — read-only when the cart is bound to one; a
            REQUIRED searchable picker for collection-link carts. */}
        {cartSchool?.name && (
          <div className="checkout__form-row">
            <div className="checkout__form-cell">
              <Input
                label="Skola"
                value={cartSchool.name}
                showIcon={false}
                readOnly
              />
            </div>
          </div>
        )}
        {needsSchoolPicker && (
          <div className="checkout__form-row">
            <div className="checkout__form-cell">
              <Input
                type="searchable"
                label="Skola"
                name="school"
                value={schoolSearch}
                options={SCHOOL_OPTIONS}
                onChange={handleSchoolSearchChange}
                onSelect={handleSchoolSelect}
                showIcon={false}
                showOptionIcons={false}
                showSuccess={Boolean(selectedSchoolId)}
                error={fieldError('schoolId')}
                onFocus={handleFocus('schoolId')}
              />
            </div>
          </div>
        )}

        {/* Row 1: Personnummer + clear */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              label="Personnummer"
              value={personnummer}
              showIcon={false}
              showClear
              onChange={(e) => {
                if (e.target.value === '') handleClearPersonnummer();
              }}
              readOnly
            />
          </div>
        </div>

        {/* Row 2: First name | Last name */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-first-name"
              label="Förnamn"
              name="fname"
              autoComplete="shipping given-name"
              value={formData?.firstName || ''}
              showIcon={false}
              showSuccess={isFieldValid('firstName')}
              error={fieldError('firstName')}
              onChange={updateField('firstName')}
              onBlur={handleBlur('firstName')}
              onFocus={handleFocus('firstName')}
            />
          </div>
          <div className="checkout__form-cell">
            <Input
              id="shipping-last-name"
              label="Efternamn"
              name="lname"
              autoComplete="shipping family-name"
              value={formData?.lastName || ''}
              showIcon={false}
              showSuccess={isFieldValid('lastName')}
              error={fieldError('lastName')}
              onChange={updateField('lastName')}
              onBlur={handleBlur('lastName')}
              onFocus={handleFocus('lastName')}
            />
          </div>
        </div>

        {/* Row 3: Email | Phone */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-email"
              type="email"
              label="E-post"
              name="email"
              autoComplete="shipping email"
              value={formData?.email || ''}
              showIcon={false}
              showSuccess={isFieldValid('email')}
              error={fieldError('email')}
              onChange={updateField('email')}
              onBlur={handleBlur('email')}
              onFocus={handleFocus('email')}
            />
          </div>
          <div className="checkout__form-cell">
            <Input
              id="shipping-phone"
              type="tel"
              label="Telefon"
              name="phone"
              autoComplete="shipping tel"
              value={formData?.phone || ''}
              showIcon={false}
              showSuccess={isFieldValid('phone')}
              error={fieldError('phone')}
              onChange={updateField('phone')}
              onBlur={handleBlur('phone')}
              onFocus={handleFocus('phone')}
            />
          </div>
        </div>

        {/* Row 4: Address (full width) */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-address"
              label="Adress"
              name="street-address"
              autoComplete="shipping street-address"
              value={formData?.address || ''}
              showIcon={false}
              showSuccess={isFieldValid('address')}
              error={fieldError('address')}
              onChange={updateField('address')}
              onBlur={handleBlur('address')}
              onFocus={handleFocus('address')}
            />
          </div>
        </div>

        {/* Row 5: Postnummer | City */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            <Input
              id="shipping-postal-code"
              label="Postnummer"
              name="postal-code"
              autoComplete="shipping postal-code"
              value={formData?.postnummer || ''}
              showIcon={false}
              showSuccess={isFieldValid('postnummer')}
              error={fieldError('postnummer')}
              onChange={updateField('postnummer')}
              onBlur={handleBlur('postnummer')}
              onFocus={handleFocus('postnummer')}
            />
          </div>
          <div className="checkout__form-cell">
            <Input
              id="shipping-city"
              label="Stad"
              name="city"
              autoComplete="shipping address-level2"
              value={formData?.city || ''}
              showIcon={false}
              showSuccess={isFieldValid('city')}
              error={fieldError('city')}
              onChange={updateField('city')}
              onBlur={handleBlur('city')}
              onFocus={handleFocus('city')}
            />
          </div>
        </div>

        {/* Row 6: Class name — searchable dropdown with "not in list" escape hatch */}
        <div className="checkout__form-row">
          <div className="checkout__form-cell">
            {!classCustomMode ? (
              <Input
                type="searchable"
                label="Klassens namn"
                name="class-name"
                value={classSearch}
                options={CLASS_OPTIONS}
                onChange={handleClassSearchChange}
                onSelect={handleClassSelect}
                showIcon={false}
                showOptionIcons={false}
                showSuccess={isFieldValid('className')}
                error={fieldError('className')}
                onFocus={handleFocus('className')}
              />
            ) : (
              <Input
                label="Klassens namn"
                name="class-name"
                value={formData?.className || ''}
                showIcon={false}
                showClear
                showSuccess={isFieldValid('className')}
                error={fieldError('className')}
                onChange={handleClassCustomChange}
                onBlur={handleBlur('className')}
                onFocus={handleFocus('className')}
              />
            )}
          </div>
        </div>
      </div>

      {renderPrivacyLink()}

      {/* Sales-rep referral tick — centered above the CTA */}
      {salesRep && (
        <div className="checkout__rep-tick">
          <CheckboxMedium
            title={`Jag blev kontaktad av ${salesRep.name}`}
            checked={contactedByRep}
            onChange={(e) => setContactedByRep(e.target.checked)}
          />
        </div>
      )}

      <div className="checkout__cta">
        <Button
          type="submit"
          size="primary"
          variant="alternative"
          iconRight={<ArrowRight size={20} />}
        >
          Fortsätt
        </Button>
      </div>

      {renderTotalPrice()}
    </form>
  );

  /* ── Step 3: Compressed + Delivery + Payment ── */
  const renderStep3 = () => (
    <div className={`checkout__step3${leaving ? ' checkout__step-wrap--leaving' : ''}`}>
      {/* Your Information */}
      <div className="checkout__step3-section">
        <h4 className="checkout__section-title typo-h4">Din information</h4>
        <div className="checkout__compressed">
          <div className="checkout__compressed-details">
            <div className="checkout__compressed-icon-wrap">
              <FileText size={32} strokeWidth={1.5} />
            </div>
            <div className="checkout__compressed-copy">
              <span className="checkout__compressed-name typo-h4">
                {formData?.firstName} {formData?.lastName}
              </span>
              <div className="checkout__compressed-lines">
                <p>{personnummer}</p>
                <p>{formData?.address}, {formData?.city}, {formData?.postnummer}, Sverige</p>
                <p>{formData?.phone}, {formData?.email}</p>
                {(cartSchool?.name || selectedSchool?.name) && (
                  <p>{cartSchool?.name ?? selectedSchool?.name}{formData?.className ? `, ${formData.className}` : ''}</p>
                )}
                {!(cartSchool?.name || selectedSchool?.name) && formData?.className && <p>{formData.className}</p>}
              </div>
            </div>
          </div>
          <Button
            size="tertiary"
            variant="gray"
            iconOnly
            iconLeft={<SquarePen size={20} />}
            onClick={handleEditInfo}
          />
        </div>
        {renderPrivacyLink()}
      </div>

      {/* Delivery selection hidden — to be re-enabled later */}

      {/* Payment — hidden entirely for fully-discounted (free) orders:
          the BE places the order as paid without any gateway */}
      {!isFreeOrder && (
      <div className="checkout__step3-section">
        <h4 className="checkout__section-title typo-h4">Betalning</h4>
        {brandSettings?.loading ? (
          <p className="checkout__payment-loading typo-body">Laddar betalningsmetoder…</p>
        ) : paymentOptions.length === 0 ? (
          <p className="checkout__error typo-body">
            {NO_PAYMENT_METHODS_MESSAGE}
          </p>
        ) : (
          <div className="checkout__radio-card">
            {paymentOptions.map((opt, i) => (
              <React.Fragment key={opt.id}>
                {i > 0 && <div className="checkout__radio-divider" />}
                <div
                  className={`checkout__radio-option${payment === opt.id ? ' checkout__radio-option--selected' : ''}`}
                  onClick={() => setPayment(opt.id)}
                  role="radio"
                  aria-checked={payment === opt.id}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setPayment(opt.id)}
                >
                  <div className="checkout__radio-main">
                    <div className="checkout__radio-circle">
                      <div className="checkout__radio-dot" />
                    </div>
                    <span className="checkout__radio-label typo-h4">{opt.label}</span>
                  </div>
                  <p className="checkout__radio-description typo-body">{opt.description}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      )}

      {/* CTA */}
      <div className="checkout__cta checkout__cta--step3">
        <Button
          size="primary"
          variant="alternative"
          iconRight={<ArrowRight size={20} />}
          onClick={handleCheckout}
          loading={checkingOut}
          disabled={!isFreeOrder && !payment}
        >
          Fortsätt
        </Button>
        {checkoutError && (
          <p className="checkout__error checkout__error--top-gap typo-body">
            {checkoutError}
          </p>
        )}
      </div>

      {renderTotalPrice()}
    </div>
  );

  /* ══════════════════════════════════════════════════════════════════════ */
  /* Main render                                                          */
  /* ══════════════════════════════════════════════════════════════════════ */
  return (
    <div className="checkout">
      <Header brand="CLS" mode="checkout" checkoutLabel="Säker Betalning" backLabel="Tillbaka" hideOnScroll={false} onBackClick={() => navigate('/clothing/cls-se/shopping-cart')} />

      <main className="checkout__main">
        <div className="checkout__content">
          <h2 className="checkout__title typo-h2-editorial">Kassa</h2>
          {step !== 3 && <h4 className="checkout__subtitle typo-h4">Din information</h4>}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && !iframePayment && renderStep3()}
          {step === 3 && iframePayment && (
            <div className="checkout__iframe-wrapper">
              <h4 className="checkout__section-title typo-h4">Slutför betalning</h4>
              <KustomCheckoutFrame
                loadingText="Skickar dig till betalning…"
                snippet={iframePayment.snippet}
                onMessage={(e) => {
                  if (e?.data?.type === 'checkout_complete' || e?.data?.event === 'checkout_complete') {
                    navigate(`/clothing/cls-se/thank-you?order=${encodeURIComponent(iframePayment.orderNumber)}&status=success`, {
                      state: { orderNumber: iframePayment.orderNumber },
                    });
                  }
                }}
              />
              <div style={{ marginTop: 16 }}>
                <Hyperlink onClick={cancelIframePayment}>Avbryt och välj annan betalning</Hyperlink>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="checkout__footer">
        <p className="checkout__footer-copy typo-body-sm">{copyright}</p>
      </footer>

      <OrderDetailsPanel
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        brandSlug={BRAND_SLUG}
        priceSuffix=":-"
        title="Orderdetaljer"
        labels={{ products: 'Produkter', packages: 'Paket', discount: 'Rabatt', total: 'Totalt' }}
        fee={selectedFee}
        feeLabel={selectedPaymentOption?.feeLabel ?? 'Fakturaavgift'}
        totalDisplay={displayPrice}
      />

      <SidePanel isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title={PRIVACY_TITLE}>
        <div className="checkout__privacy-content">
          <LegalSections sections={PRIVACY_SECTIONS} />
        </div>
      </SidePanel>
    </div>
  );
};

export default Checkout;
